import i18next, {DefaultNamespace, Namespace, TOptions} from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import {initReactI18next, Trans as i18nTrans, TransProps} from 'react-i18next';
import type {ReactElement} from 'react';

import {isTestEnv} from 'common/report/util/env';
import {reporter} from 'common/report/error-reporting';
import {localStorageManager} from 'common/contexts/storage-manager';

import type {AnyScopedLocalizationStore, LocalizationStore} from './store';
import {LOCALES} from './locales';

const STORAGE_KEY = `selectedLanguage`;
const DEFAULT_NS = `translation`;

function getDefaultLocale() {
  const stored = typeof window !== `undefined` ? localStorageManager.getItem(STORAGE_KEY) : null;
  if (!isTestEnv() && stored) {
    return stored;
  }

  return `en`;
}

type TranslationBundle = Record<string, string>;
type BundleLoader = () => Promise<TranslationBundle>;

/** Locales with a machine-translated bundle of every scope key. English has none: scopes register it. */
const GENERATED_LOCALES = new Set([`es`, `fr`, `id`, `ja`, `ko`, `pt`, `zh-CN`, `zh-TW`]);

/** Resolves to a bundle path only for locales in GENERATED_LOCALES, so the dynamic import below cannot be steered elsewhere. */
function getGeneratedBundleFileName(language: string): string {
  if (!GENERATED_LOCALES.has(language)) {
    throw new Error(`No generated translations for ${language}`);
  }
  return language;
}

/** The variable path keeps the type checker from loading the large JSON files as typed modules. */
async function loadGeneratedBundle(language: string): Promise<TranslationBundle> {
  if (!GENERATED_LOCALES.has(language)) {
    return {};
  }
  const module = await import(`./locales/generated/${getGeneratedBundleFileName(language)}.json`);
  return module.default;
}

/** Hand-written overrides that win over the generated translations. Locales without a file have none. */
const OVERRIDE_LOADERS: Record<string, BundleLoader> = {
  es: () => import(`./locales/es`).then((module) => module.default),
  fr: () => import(`./locales/fr`).then((module) => module.default),
  id: () => import(`./locales/id`).then((module) => module.default),
  ja: () => import(`./locales/ja`).then((module) => module.default),
  ko: () => import(`./locales/ko`).then((module) => module.default),
  'zh-CN': () => import(`./locales/zh-CN`).then((module) => module.default),
  'zh-TW': () => import(`./locales/zh-TW`).then((module) => module.default),
};

async function loadLocaleBundle(language: string): Promise<TranslationBundle> {
  const [generated, overrides] = await Promise.all([
    loadGeneratedBundle(language),
    OVERRIDE_LOADERS[language]?.() ?? {},
  ]);
  return {...generated, ...overrides};
}

i18next
  .use(resourcesToBackend(loadLocaleBundle))
  .use(initReactI18next) // bind react-i18next to the instance
  .init({
    lng: getDefaultLocale(),
    fallbackLng: `en`,
    // not supporting saving missing, but needs to be true to enable missingKey event
    saveMissing: true,
    defaultNS: DEFAULT_NS,
    interpolation: {
      // Translated text with user input is never rendered directly in the dom (React already handles xss sanitization).
      // Double-escaping leads to rendering issues for names with special characters
      escapeValue: false,
    },
  });

// https://www.i18next.com/translation-function/formatting#adding-custom-format-function
i18next.services.formatter?.add(`lowercase`, (value) => {
  return value.toLowerCase();
});

i18next.on(`languageChanged`, (lng) => {
  if (typeof window !== `undefined`) {
    localStorageManager.setItem(STORAGE_KEY, lng);
  }
});

i18next.on(`missingKey`, (_lng, _namespace, key, _fallbackValue) => {
  reporter.error(`Missing i18n entry for ${key}`);
});

export function changeLanguage(code: string) {
  i18next.changeLanguage(code);
}

// this is a little hack that makes i18next conditional types work w/o strictNullChecks enabled
type KeyPrefix = 0;

declare const LOCALE_SAFE_BRAND: unique symbol;

/**
 * String safe to display in any locale — either produced by `t(...)` or marked
 * safe via `markLocaleSafe` because the source guarantees display-safety
 * regardless of locale (e.g. a backend that honors Accept-Language, an empty
 * string, a proper noun). Assignable to plain `string`, so it flows through
 * the rest of the codebase unchanged.
 *
 * Branded so that `StrictTranslatable` callbacks and props typed as
 * `LocaleSafeString` can't be satisfied by a raw untranslated literal.
 */
export type LocaleSafeString = string & {readonly [LOCALE_SAFE_BRAND]: true};

/** Escape hatch for strings whose source guarantees locale-safety. */
export function markLocaleSafe(value: string): LocaleSafeString {
  return value as LocaleSafeString;
}

/**
 * The store a translatable is run with: whichever store `print` was invoked on. A scoped store only accepts the
 * keys of its own scope, so a translatable written elsewhere translates with `translatable(scope, render)`.
 */
export type TranslationStore = LocalizationStore | AnyScopedLocalizationStore;
export type Translatable = string | ((i18n: TranslationStore) => string);
export type StrictTranslatable = (i18n: TranslationStore) => LocaleSafeString;

// // if we ever get to strictNullChecks, can remove this retyping and use react-i18next's type
type TransFunc = <
  TranslateFunction extends (key: never, opts?: TOptions) => LocaleSafeString,
  Ns extends Namespace = DefaultNamespace,
  KPrefix = KeyPrefix,
  TContext extends string | undefined = undefined,
  TOpt extends TOptions & {context?: TContext} = {context: TContext},
  E = React.HTMLProps<HTMLDivElement>,
>(
  props: Omit<TransProps<string, Ns, KPrefix, TContext, TOpt, E>, `i18nKey` | `t`> & {
    /** The `t` of a scoped store (keys of that scope) or its `translateKey` (keys from `scope.qualify`). */
    t: TranslateFunction;
    i18nKey: Parameters<TranslateFunction>[0];
  },
) => ReactElement;

export const Trans = i18nTrans as TransFunc;

export {i18next, LOCALES};
